import {
  getClientLocationDetails,
  getProductBreadcrumbItems,
  useAppContext,
  useBranchLocation,
  useCartContext,
  useChatbot,
  useShallow,
  useWebSocket,
} from "application";
import {
  ButtonSubmit,
  PopupAlertAddProductToWeddingList,
  PopUpAlertConfirm,
} from "components";
import {
  appMonitoringClient,
  cookie,
  crm,
  gtag,
  weddingGiftListApi,
} from "implementations";
import { useRouter } from "next/router";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import {
  AddProductResponseDto,
  ControlledCartAddProductResponse,
  Price,
  Product,
} from "typing";
import { Row, SpaceWrapper, theme } from "ui";
import {
  cookieAccessToken,
  cookieBranchLocationStr,
  keyZipCodeStr,
  LIST_ENVIRONMENT_DEFAULT,
  SECTIONS_ITEMS_MENU,
} from "../../utils/constants";
import { BreadcrumbProduct, ModalVoltage } from "./components";
import {
  MainProductContainer,
  ProductContainer,
  WrapperLoading,
} from "./Product.styles";
import { ProductPageUIProps } from "./Product.types";
import AboutTheProduct from "./sections/AboutTheProduct";
import AlsoSee from "./sections/AlsoSee";
import BoxProduct from "./sections/BoxProduct";
import HeaderProductFixed from "./sections/HeaderProductFixed";
import ProductSpec from "./sections/ProductSpec";

export const ProductPageUI = ({
  blackFridayTheme,
  cartId,
  hasChangesInClientBranchFromRegionIdParam,
  siteUrl,
  dataProduct,
  productPrices,
  activeWeddingList,
  branchId,
  productId,
}: ProductPageUIProps) => {
  const { setShowSaleChatbot } = useChatbot(
    useShallow((state) => ({
      setShowSaleChatbot: state.setShowSaleChatbot,
    }))
  );
  const router = useRouter();

  const accessToken = cookie.getCookie({
    name: cookieAccessToken,
  }) as string;

  const { setDefaultBranchLocation } = useBranchLocation();

  const cart = useCartContext();

  const [dataProductDetails, setDataProduct] = useState<Product>();
  const [packageCurrentState, setPackageCurrent] = useState<Price>();
  const [isVoltageModalOpen, setIsVoltageModalOpen] = useState(false);
  const [firstRender, setFirstRender] = useState(true);
  const [isWeddingListPopUpOpened, setIsWeddingListPopUpOpened] =
    useState(false);
  const [weddingListModalError, setWeddingListModalError] = useState(false);
  const [quantityItems, setQuantityItems] = useState(1);
  const [limitQuantityItem, setLimitQuantityItem] = useState<number>();
  const [sendingItemToCart, setSendingItemToCart] = useState(false);
  const [isLoadingOpen, setIsLoadingOpen] = useState(false);

  const addProductsToWeddingList = useCallback(async () => {
    try {
      if (!dataProduct || quantityItems === 0 || !activeWeddingList?.id) {
        return;
      }
      const response = (await weddingGiftListApi.addProductsToWeddingList(
        accessToken,
        [
          {
            packaging: dataProduct?.packingLevel || 0,
            productId: dataProduct?.id || "",
            listId: Number(activeWeddingList?.id),
            listEnviroment: LIST_ENVIRONMENT_DEFAULT,
            productQuantity: quantityItems,
            branchId,
          },
        ]
      )) as unknown as AddProductResponseDto;

      if (response?.isValid) {
        setIsWeddingListPopUpOpened(true);
      } else {
        setWeddingListModalError(true);
      }
    } catch (error) {
      appMonitoringClient.captureException(error, { level: "error" });
    }
  }, [
    dataProduct,
    quantityItems,
    activeWeddingList?.id,
    accessToken,
    branchId,
  ]);

  const price =
    packageCurrentState?.spotPrice ||
    (packageCurrentState?.salePrice !== packageCurrentState?.priceList
      ? packageCurrentState?.salePrice
      : packageCurrentState?.priceList);

  const [
    clientIsWaitingActionAfterClickOnSendItemToCartButton,
    setClientIsWaitingActionAfterClickOnSendItemToCartButton,
  ] = useState(false);

  const sendItemToCart = useCallback(() => {
    setClientIsWaitingActionAfterClickOnSendItemToCartButton(false);

    const dataAnalytic = () => {
      if (!dataProduct) {
        return;
      }
      gtag.addToCart(price as number, dataProduct);
    };

    const VALIDATE_VOLTAGE =
      dataProductDetails?.voltage !== " " &&
      Boolean(dataProductDetails?.voltage);

    if (VALIDATE_VOLTAGE && !isVoltageModalOpen) {
      setIsVoltageModalOpen(true);
      setIsLoadingOpen(false);
      setSendingItemToCart(false);
      return;
    }

    setSendingItemToCart(true);
    setIsLoadingOpen(true);

    cart
      .addProductToControlledCart({
        productId: dataProduct?.id || "",
        quantity: quantityItems,
        package: packageCurrentState?.packingLevel || 0,
        packingQuantity: packageCurrentState?.packingQuantity || 0,
        sellerId: packageCurrentState?.sellerId || 0,
        cartId: Number(cartId),
        price: packageCurrentState?.salePrice as number,
      })
      .then((res) => {
        setIsVoltageModalOpen(false);
        const response = res as unknown as ControlledCartAddProductResponse;

        if (response?.limitTotalItens) {
          setLimitQuantityItem(Number(response.limitTotalItens));
          setSendingItemToCart(false);
          setIsLoadingOpen(false);
          return;
        }

        if (response?.hasError) {
          setSendingItemToCart(false);
          setIsLoadingOpen(false);
          return;
        }

        router.push("/checkout/meu-carrinho");
        setSendingItemToCart(false);
        dataAnalytic();
      })
      .catch(() => {
        setSendingItemToCart(false);
        setIsLoadingOpen(false);
      });
  }, [
    cart,
    cartId,
    dataProduct,
    dataProductDetails?.voltage,
    isVoltageModalOpen,
    packageCurrentState?.packingLevel,
    packageCurrentState?.packingQuantity,
    packageCurrentState?.salePrice,
    packageCurrentState?.sellerId,
    price,
    quantityItems,
    router,
  ]);

  useEffect(() => {
    if (
      packageCurrentState?.packingQuantity &&
      clientIsWaitingActionAfterClickOnSendItemToCartButton
    ) {
      sendItemToCart();
    }
  }, [
    clientIsWaitingActionAfterClickOnSendItemToCartButton,
    packageCurrentState?.packingQuantity,
    sendItemToCart,
  ]);

  const handleSendItemToCart = useCallback(
    (packingQuantity: number) => {
      setClientIsWaitingActionAfterClickOnSendItemToCartButton(true);
      setSendingItemToCart(true);
      if (packingQuantity) {
        sendItemToCart();
      }
    },
    [sendItemToCart]
  );

  useEffect(() => {
    if (dataProduct) {
      setDataProduct(dataProduct);
    }
  }, [dataProduct, productId]);

  useLayoutEffect(() => {
    if (!Number.isNaN(branchId)) {
      setDefaultBranchLocation(String(branchId));
    }
  }, [branchId, setDefaultBranchLocation]);

  const { emitEvent } = useWebSocket();

  const handleHasChangesInClientBranchFromRegionIdParam = useCallback(
    (hasChanges: boolean, newBranchId: number) => {
      if (hasChanges) {
        const currentClientLocation = getClientLocationDetails(cookie);
        emitEvent("branchChanged", {
          ...currentClientLocation,
          clientBranchId: newBranchId,
          clientZipCode: "",
        });
        cookie.setCookie({
          name: cookieBranchLocationStr,
          value: String(branchId),
        });
        localStorage?.removeItem(keyZipCodeStr);
      }
    },
    [emitEvent, branchId]
  );

  useEffect(() => {
    if (dataProduct && firstRender && productPrices) {
      gtag.viewItem(
        productPrices?.[0]?.spotPrice ||
          (productPrices?.[0]?.salePrice as number),
        dataProduct
      );

      crm.pageObject("Product");

      handleHasChangesInClientBranchFromRegionIdParam(
        hasChangesInClientBranchFromRegionIdParam,
        branchId
      );

      setFirstRender(false);
    }
  }, [
    branchId,
    dataProduct,
    firstRender,
    handleHasChangesInClientBranchFromRegionIdParam,
    hasChangesInClientBranchFromRegionIdParam,
    productPrices,
  ]);

  const breadcrumbItems = getProductBreadcrumbItems(dataProduct);

  const { isClientMobile, setPageThemeMap } = useAppContext();

  // Refatorar renderização do componente de ProductSpec para lógica do Insider Object.
  const [productSize, setProductSize] = useState<string>();
  const [productModel, setProductModel] = useState<string>();

  const [firstRenderInsiderObjectProduct, setFirstRenderInsiderObjectProduct] =
    useState<boolean>(true);

  const [hasCalledProductSpecs, setHasCalledProductSpecs] =
    useState<boolean>(true);

  const taxonomy = breadcrumbItems?.items?.common.map((item) => {
    return item.description;
  });

  const selectedProductColor = dataProduct?.productFamilies?.[0]?.families.find(
    (item) => item.selectedValue
  );

  const handleSize = (size: string) => {
    setProductSize(size);
  };

  const handleModel = (model: string) => {
    setProductModel(model);
  };

  const hasProductSpecCalled = (value: boolean) => {
    setHasCalledProductSpecs(value);
  };

  useEffect(() => {
    setPageThemeMap((currentPageThemeMap) => ({
      ...currentPageThemeMap,
      product: blackFridayTheme?.isBlackTheme ? "black" : "default",
    }));
  }, [blackFridayTheme, setPageThemeMap]);

  useEffect(() => {
    const regexToRemoveTheFirstSlash = /^\/(.*)/;
    if (
      firstRenderInsiderObjectProduct &&
      !hasCalledProductSpecs &&
      dataProduct &&
      taxonomy &&
      productPrices
    ) {
      const productCrmProps = {
        product_id: dataProduct?.id,
        name: dataProduct?.description,
        taxonomy: taxonomy?.slice(0, 3) as string[],
        url: `${siteUrl}${router.asPath.replace(
          regexToRemoveTheFirstSlash,
          "$1"
        )}`,
        image_url:
          dataProduct?.mediaLinks?.find(
            (link) => link.isDefault && link.linkType === "IMAGEM"
          )?.imageUrl || "",
        price: packageCurrentState?.priceList || 0,
        currency: "BRL",
        color: selectedProductColor?.color?.name || "",
        voucherName: dataProduct?.coupons?.[0]?.couponName || "",
        size: productSize || "",
        salePrice:
          packageCurrentState?.salePrice || packageCurrentState?.priceList || 0,
        spotPrice:
          packageCurrentState?.spotPrice || packageCurrentState?.salePrice || 0,
        voucherDiscountPercentage:
          dataProduct?.coupons?.[0]?.discountPercentage || 0,
        voucherDiscountValue: dataProduct?.coupons?.[0]?.discountValue || 0,
        product_tag: dataProduct?.tag?.title || "",
        branch_id: branchId.toString(),
        model: productModel || "",
        brand: dataProduct?.brand || "",
      };
      crm.productObject(productCrmProps);

      // crm.reinitializeTag();
      setFirstRenderInsiderObjectProduct(false);
    }
  }, [
    branchId,
    dataProduct,
    firstRenderInsiderObjectProduct,
    hasCalledProductSpecs,
    packageCurrentState?.priceList,
    packageCurrentState?.salePrice,
    packageCurrentState?.spotPrice,
    productModel,
    productPrices,
    productSize,
    router.asPath,
    selectedProductColor?.color?.name,
    siteUrl,
    taxonomy,
  ]);

  const getImageUrl = () => {
    const mediaLinks = dataProduct?.mediaLinks;

    if (mediaLinks) {
      const imageLink = mediaLinks.find((link) => link.linkType === "IMAGEM");

      if (imageLink) {
        return imageLink.imageUrl;
      }
    }

    return "";
  };

  useEffect(() => {
    if (accessToken && dataProduct?.isChatbotEnabled) {
      setShowSaleChatbot(true);
      return;
    }

    setShowSaleChatbot(false);
  }, [setShowSaleChatbot, accessToken, dataProduct]);

  useEffect(() => {
    return () => {
      setShowSaleChatbot(false);
    };
  }, [setShowSaleChatbot]);

  return (
    <>
      <HeaderProductFixed
        isBlackTheme={blackFridayTheme?.isBlackTheme}
        {...{ ...dataProduct, idProduct: String(productId) }}
        packageCurrentState={packageCurrentState}
        addItemCart={() =>
          handleSendItemToCart(packageCurrentState?.packingQuantity || 0)
        }
        sectionIdItemsMenu={SECTIONS_ITEMS_MENU}
        actionAddToGift={addProductsToWeddingList}
        activeButtonWeddingList={Boolean(activeWeddingList?.id)}
        sendingItemToCart={sendingItemToCart}
        maximumStockPerBranch={dataProduct?.maximumStockPerBranch ?? 0}
      />

      <MainProductContainer isBlackTheme={blackFridayTheme?.isBlackTheme}>
        <ProductContainer>
          <Row>
            <SpaceWrapper
              padding={`${theme.space.x5} ${theme.space.x0} ${theme.space["x3.5"]}`}
            >
              <BreadcrumbProduct items={breadcrumbItems?.items || null} />
            </SpaceWrapper>
          </Row>
          <BoxProduct
            isBlackTheme={!!dataProduct?.isBlackFriday}
            {...{
              ...dataProduct,
              idProduct: String(productId),
              prices: productPrices,
            }}
            setPackageCurrent={setPackageCurrent}
            packageCurrentState={packageCurrentState}
            actionAddItemCart={() =>
              handleSendItemToCart(packageCurrentState?.packingQuantity || 0)
            }
            setProductQuantity={setQuantityItems}
            actionAddToGift={addProductsToWeddingList}
            activeButtonWeddingList={Boolean(activeWeddingList?.id)}
            limitTotalQuantityItem={limitQuantityItem}
            sendingItemToCart={sendingItemToCart}
            isBlackFriday={blackFridayTheme?.isBlackTheme}
          />
          {isLoadingOpen && (
            <WrapperLoading isClientMobile={isClientMobile}>
              <ButtonSubmit
                underAction
                color="transparent"
                contentColor={theme.colors.secondary["300"]}
                height="4rem"
                minWidth="200px"
                buttonType="button"
                hoveredColor="trasnparent"
                textUnderAction="Carregando"
                border="2px solid"
                borderColor={theme.colors.secondary["300"]}
              />
            </WrapperLoading>
          )}
          {dataProduct ? (
            <>
              {dataProduct?.detailedDescription ? (
                <AboutTheProduct
                  idSection={SECTIONS_ITEMS_MENU[0].idReference}
                  productDetails={dataProduct?.detailedDescription}
                  isBlackTheme={blackFridayTheme?.isBlackTheme}
                />
              ) : null}
              <ProductSpec
                idSection={SECTIONS_ITEMS_MENU[1].idReference}
                brand={dataProduct?.brand}
                idProduct={String(productId)}
                handleSize={handleSize}
                handleModel={handleModel}
                hasProductSpecCalled={hasProductSpecCalled}
                isBlackTheme={blackFridayTheme?.isBlackTheme}
                bareanId13={dataProduct?.bareanId13}
              />
              <AlsoSee
                idSection={SECTIONS_ITEMS_MENU[2].idReference}
                idProduct={String(productId)}
                isBlackTheme={blackFridayTheme?.isBlackTheme}
              />
            </>
          ) : null}
        </ProductContainer>
      </MainProductContainer>

      <ModalVoltage
        stateModal={isVoltageModalOpen}
        actionCloseModal={() => setIsVoltageModalOpen(false)}
        actionSuccess={() =>
          handleSendItemToCart(packageCurrentState?.packingQuantity ?? 0)
        }
        sendingItemToCart={sendingItemToCart}
        infoText={dataProduct?.voltage}
      />

      <PopupAlertAddProductToWeddingList
        textDescriptionProduct={dataProduct?.description}
        titleAlert="ADICIONADO À LISTA"
        imageUrlProduct={getImageUrl()}
        isOpen={isWeddingListPopUpOpened}
        handlerStatusPopup={setIsWeddingListPopUpOpened}
      />

      <PopUpAlertConfirm
        textHeader="Falha ao adicionar produto"
        isOpen={weddingListModalError}
        actionCloseButton={() => setWeddingListModalError(false)}
      />
    </>
  );
};
